import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

/**
 * App-only inputs for the note-suggestions surface. The mounts bind this once
 * via `configureNoteSuggestionsUi`; the hooks read it and never import the app.
 */
export type NoteSuggestionsDocumentDiffChange =
  | { type: 'insert' | 'format'; from: number; to: number }
  | { type: 'delete'; pos: number; text: string };

export interface NoteSuggestionsUiHost {
  /** The app `useMutateSyncNoteToServer` delegate, reactions included. */
  useSyncNoteToServer: () => {
    mutate: (input: { consultNote: string; sessionId: string }) => void;
  };
  /** True while the offer card is current or the graduation hold is up. */
  useIsOptInPreviewAllowed: () => boolean;
  isEditorLive: (
    editor: Editor | null | undefined,
    callSite?: string
  ) => editor is Editor;
  whenEditorLive: (
    editor: Editor | null | undefined,
    callSite: string,
    fn: (editor: Editor) => void
  ) => void;
  computeDocumentDiffChanges: (
    beforeDoc: ProseMirrorNode,
    afterDoc: ProseMirrorNode,
    granularity?: 'character' | 'word'
  ) => NoteSuggestionsDocumentDiffChange[];
}

let host: NoteSuggestionsUiHost | null = null;

export const configureNoteSuggestionsUi = (next: NoteSuggestionsUiHost): void => {
  host = next;
};

/** Fail loudly when a mount forgot to register the host. */
export const useNoteSuggestionsUiHost = (): NoteSuggestionsUiHost => {
  if (!host) {
    throw new Error(
      'note-suggestions host is not configured — call configureNoteSuggestionsUi() from a mount before rendering this surface'
    );
  }
  return host;
};

/** Test-only: drops the registered host so a suite can assert the failure. */
export const __resetNoteSuggestionsUiHostForTests = (): void => {
  host = null;
};
